import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon16DownloadOutline, Icon16Dropdown, Icon20DownloadOutline, Icon20Dropdown } from '@vkontakte/icons';
import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import classNames from 'clsx';
import { isNil } from 'ramda';
import React, { type ReactElement, memo } from 'react';
import { IS_BIZ_USER, IS_CORP_USER, IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import { enabledPublicDownloadLimits } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { type ListItem, DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { useDropdownList } from 'reactApp/ui/DropdownList/DropdownList.hooks';
import { Hint } from 'reactApp/ui/Hint/Hint';

import styles from './Download.css';

interface DownloadProps {
    isAchivedLimit?: boolean;
    onClick: React.MouseEventHandler<HTMLDivElement>;
    size?: number;
    ext?: string;
    id: string;
    storage?: EStorageType;
    show?: boolean;
    isLarge?: boolean;
    animate?: boolean;
    viewType: EViewMode;
    className?: string;
    enableDownloadAsJpeg?: boolean;
}

export const Download = memo<DownloadProps>(function Download({
    isAchivedLimit,
    onClick,
    ext,
    size,
    id,
    storage,
    animate = false,
    viewType,
    className,
    isLarge = true,
    enableDownloadAsJpeg = false,
}) {
    const { controlRef, findDropdownPosition, isOpen, open, close } = useDropdownList<HTMLDivElement>();

    const toggleDropdown = () => {
        if (isOpen) {
            close();
        } else {
            open();
        }
    };

    const sizeTitle = isNil(size) ? undefined : bytesToNDigits(size, 3).value;
    const downloadHint = `Скачать${ext ? ` .${ext.toLowerCase()}` : ''}${sizeTitle ? `${ext ? `, ${sizeTitle}` : ` ${sizeTitle}`}` : ''}`;

    const renderDropdownItem = (item: ListItem): ReactElement => {
        return <DropdownItemAction text={item.text} />;
    };

    const downloadAsJpegAvailable =
        ext && enableDownloadAsJpeg && downloadAsJpeg.isAvailable({ ext, storage }) && storage !== EStorageType.archive;

    const DownloadIcon = isLarge ? Icon20DownloadOutline : Icon16DownloadOutline;
    const DownloadAsJpegIcon = isLarge ? Icon20Dropdown : Icon16Dropdown;

    const iconStyle = {
        color: enabledPublicDownloadLimits && isAchivedLimit && !IS_PAID_USER && !IS_BIZ_USER && !IS_CORP_USER ? '#FF5C5C' : 'inherit',
    };

    const noPublic = storage !== EStorageType.public;

    return (
        <>
            {!isAchivedLimit || noPublic ? (
                <div ref={controlRef} className={classNames(styles.root, className)}>
                    <Hint text={downloadHint}>
                        <DataListControl.New
                            id="download-icon"
                            type="download"
                            animate={animate}
                            viewType={viewType}
                            onClick={onClick}
                            className={styles.icon}
                        >
                            <DownloadIcon style={iconStyle} />
                        </DataListControl.New>
                    </Hint>
                    {downloadAsJpegAvailable && (
                        <DataListControl.New
                            id="download-as-jpg-icon"
                            type="downloadAsJpeg"
                            viewType={viewType}
                            animate={animate}
                            onClick={toggleDropdown}
                            className={styles.icon}
                        >
                            <DownloadAsJpegIcon
                                className={classNames({
                                    [styles.downloadJpegIconOpen]: isOpen,
                                })}
                                style={iconStyle}
                            />
                        </DataListControl.New>
                    )}
                </div>
            ) : null}
            {downloadAsJpegAvailable && isOpen && (
                <DropdownList
                    gaId="datalist-control"
                    list={downloadAsJpeg.getDropdownItems({ itemOrId: id, gaSuffix: 'datalist', ext, textPrefix: 'в', storage })}
                    close={close}
                    closeOnScroll
                    closeOnMouseLeave
                    posX={0}
                    posY={0}
                    parentRef={controlRef}
                    theme={DropdownTheme.jpeg}
                    title={'Скачать в'}
                    calcPosition={findDropdownPosition}
                    renderItem={renderDropdownItem}
                />
            )}
        </>
    );
});
